import type { BetSelection } from "@/features/betslip/types";
import { areRelated } from "@/features/betslip/utils/rules";

// Each input selection gets wrapped in a Candidate that carries its
// tie-break score and the set of other candidates it conflicts with.
// Working in terms of Candidate objects (rather than indices into the
// original array) keeps the algorithm readable end-to-end.
type Candidate = {
  selection: BetSelection;
  score: number;
  conflicts: Set<Candidate>;
};

// Higher score wins among equal-size subsets of the slip.
//   SEM → 1, standalone single → 0
const tieBreakScore = (selection: BetSelection): number =>
  selection.kind === "sem" ? 1 : 0;

// Fill in each candidate's `conflicts` set by checking every pair.
const findConflicts = (
  candidates: Candidate[],
  options: { allowCrossProduct?: boolean },
): void => {
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      if (
        areRelated(candidates[i].selection, candidates[j].selection, options)
      ) {
        candidates[i].conflicts.add(candidates[j]);
        candidates[j].conflicts.add(candidates[i]);
      }
    }
  }
};

// Sort by highest score first, then by fewest conflicts. JS's stable
// sort keeps anything still tied in original input order.
//
// Picking low-conflict candidates first avoids the trap where a
// candidate that conflicts with everything gets in early and blocks
// many neighbors that could otherwise have coexisted.
const orderByPriority = (candidates: Candidate[]): Candidate[] =>
  [...candidates].sort((candidateA, candidateB) => {
    if (candidateA.score !== candidateB.score) {
      return candidateB.score - candidateA.score;
    }
    return candidateA.conflicts.size - candidateB.conflicts.size;
  });

// Walk in priority order. Keep each candidate unless it conflicts with
// something already kept.
const pickWithoutConflicts = (ordered: Candidate[]): Set<Candidate> => {
  const picked = new Set<Candidate>();
  for (const candidate of ordered) {
    let collides = false;
    for (const other of candidate.conflicts) {
      if (picked.has(other)) {
        collides = true;
        break;
      }
    }
    if (!collides) picked.add(candidate);
  }
  return picked;
};

/**
 * Pick the largest conflict-free subset of `selections` to form a multi.
 * Among equal-size subsets, prefers SEMs over standalone singles.
 *
 * Worked example:
 *   Input: 3 singles from event e-1 (a, b, cSame), a SEM covering e-1,
 *          plus singles c (e-2) and d (e-3).
 *   Output: [sem, c, d] — the SEM beats the individual e-1 singles by
 *          score; c and d come along because they don't conflict.
 *
 * Returns [] if no 2-leg-or-more subset is possible.
 */
export const pickMultiLegs = (
  selections: BetSelection[],
  options: { allowCrossProduct?: boolean } = {},
): BetSelection[] => {
  if (selections.length < 2) return [];

  const candidates: Candidate[] = selections.map((selection) => ({
    selection,
    score: tieBreakScore(selection),
    conflicts: new Set(),
  }));
  findConflicts(candidates, options);

  const ordered = orderByPriority(candidates);
  const picked = pickWithoutConflicts(ordered);

  if (picked.size < 2) return [];

  // Return in original input order so the slip UI stays stable.
  return candidates.filter((c) => picked.has(c)).map((c) => c.selection);
};
